import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Building2, FileText, Users, TrendingUp, AlertCircle, CheckCircle2, Calculator, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const LebensmittelmaerkteSeite = () => {
  const objektmerkmale = [
    'Verkaufsfläche: Typisch 800-2.500 m²',
    'Grundstücksgröße: 3.000-8.000 m² inkl. Parkplätze',
    'Mieter: Edeka, Rewe, Kaufland, Netto, Norma, Penny',
    'Standort: Meist integrierte Wohnlagen oder Nahversorgungszentren',
    'Bauweise: Eingeschossig, funktional, oft mit Lager-/Kühlräumen'
  ];

  const mietvertragsstruktur = [
    {
      title: 'Typische Laufzeiten',
      content: 'Erstlaufzeit 10-15 Jahre, oft mit 2x5 Jahre Verlängerungsoptionen. Bonitätsstarke Vollsortimenter (Edeka, Rewe) meist längere Laufzeiten als Discounter.'
    },
    {
      title: 'Mietanpassungen',
      content: 'Häufig Indexierung (meist 70-80% Anpassung an Verbraucherpreisindex) oder Staffelmieten mit 1,5-2,5% jährlicher Steigerung. Indexierung bei Käufern beliebter.'
    },
    {
      title: 'Besondere Klauseln',
      content: 'Optionsrechte meist einseitig beim Mieter. Aufpassen: Konkurrenzschutzklauseln können Nachvermietung erschweren. Instandhaltung meist beim Mieter (Triple Net).'
    }
  ];

  const kaeufergruppen = [
    {
      gruppe: 'Institutionelle Investoren',
      erwartungen: 'WAULT > 10 Jahre, bonitätsstarke Mieter (Edeka, Rewe), Indexierung, 1a/1b-Lage, Mindestvolumen oft 3-5 Mio. €',
      rendite: '4,0-5,0%'
    },
    {
      gruppe: 'Family Offices',
      erwartungen: 'WAULT > 8 Jahre, stabile Cashflows, gute Standorte, Volumen flexibel ab 1,5 Mio. €',
      rendite: '4,5-5,5%'
    },
    {
      gruppe: 'Privatinvestoren',
      erwartungen: 'WAULT > 5 Jahre, auch kleinere Objekte interessant, Fokus auf Rendite und Lage',
      rendite: '5,0-6,0%'
    }
  ];

  const unterlagen = [
    'Mietvertrag inkl. aller Nachträge (wichtig: Indexierung, Optionen, Konkurrenzschutz)',
    'Betriebskostenabrechnungen letzte 3 Jahre (Umlagefähigkeit prüfen)',
    'Grundbuchauszug nicht älter als 3 Monate',
    'Aktuelle Flächenpläne mit Flächenberechnung',
    'Energieausweis (Verbrauch oder Bedarf)',
    'Baugenehmigung und Teilungsgenehmigung',
    'Nachweis Mietzahlungen letzte 12 Monate',
    'Lageplan mit Parkplätzen und Anlieferung'
  ];

  const fehler = [
    {
      fehler: 'Mietverträge unvollständig',
      folge: 'Käufer können Due Diligence nicht abschließen, Verzögerungen oder Abbruch',
      vermeidung: 'VOR Vermarktung alle Nachträge sammeln und prüfen lassen'
    },
    {
      fehler: 'WAULT nicht bekannt',
      folge: 'Falsche Preiserwartung, Käufer ziehen sich zurück',
      vermeidung: 'WAULT-Rechner nutzen, ggf. Optionen aktivieren lassen'
    },
    {
      fehler: 'Mieter nicht informiert',
      folge: 'Rechtliche Probleme wegen Vorkaufsrecht, Verzögerungen',
      vermeidung: 'Frühzeitig rechtlich beraten lassen zur korrekten Information'
    }
  ];

  const faq = [
    {
      q: 'Was macht Lebensmittelmärkte für Investoren attraktiv?',
      a: 'Lebensmittelmärkte gelten als krisensicher, da Grundversorgung. Lange Mietverträge (oft 10-15 Jahre) und bonitätsstarke Mieter (Edeka, Rewe) bieten stabile Cashflows. Bei guten Standorten und WAULT > 10 Jahren sind Renditen von 4-5% erzielbar.'
    },
    {
      q: 'Wie wird der Wert eines Lebensmittelmarkts berechnet?',
      a: 'Hauptsächlich über Ertragswertverfahren: Jahresmiete geteilt durch Kapitalisierungszinssatz (Cap Rate). Faktoren: Mieter-Bonität, WAULT, Standort, Zustand. Beispiel: 100.000€ Miete bei 5% Cap Rate = 2 Mio. € Wert. WAULT und Indexierung erhöhen den Wert.'
    },
    {
      q: 'Was ist bei der Vermarktung besonders wichtig?',
      a: 'Der WAULT ist entscheidend. Objekte mit WAULT > 10 Jahren erzielen deutlich bessere Preise. Auch wichtig: Indexierung (besser als Staffel), keine kritischen Konkurrenzschutzklauseln, aktuelle Mietzahlungen ohne Rückstände, vollständige Unterlagen.'
    },
    {
      q: 'Wie lange dauert der Verkauf eines Supermarkts?',
      a: 'Bei gut vorbereiteten Objekten (WAULT > 8 Jahre, vollständige Unterlagen) 6-9 Monate. Bei kürzeren Laufzeiten oder komplizierten Mietverträgen 9-15 Monate. Die Vorbereitung (Unterlagen, Mieterinformation) sollte nicht unterschätzt werden.'
    },
    {
      q: 'Was ist der Unterschied zwischen Vollsortimenter und Discounter?',
      a: 'Vollsortimenter (Edeka, Rewe, Kaufland) haben meist größere Flächen (1.200-2.500 m²), höhere Mieten und längere Vertragslaufzeiten. Discounter (Aldi, Lidl, Netto) haben kleinere Flächen (800-1.200 m²), standardisierte Verträge und sind bei Investoren wegen der Bonität sehr beliebt.'
    },
    {
      q: 'Was passiert wenn der Mietvertrag bald ausläuft?',
      a: 'Kurze Restlaufzeiten (WAULT < 5 Jahre) reduzieren den Verkaufspreis deutlich. Optionen: 1) Vor Verkauf Vertragsverlängerung verhandeln, 2) Verkauf mit Preisabschlag an opportunistische Käufer, 3) Abwarten und nach Verlängerung verkaufen. Frühzeitige Planung ist entscheidend.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Lebensmittelmarkt verkaufen – Supermarkt Immobilie erfolgreich vermitteln"
        description="Professionelle Vermittlung von Supermarkt-Immobilien: Edeka, Rewe, Kaufland. Expertise zu WAULT, Mietverträgen, Käufergruppen und Bewertung. Kostenloser WAULT-Rechner."
        url="/assetklassen/lebensmittelmaerkte"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero */}
        <section className="relative py-20 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6"></div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
                Lebensmittelmarkt verkaufen
              </h1>
              <p className="text-xl leading-relaxed mb-8" style={{ color: 'var(--gray-700)' }}>
                Professionelle Vermittlung von Supermarkt-Immobilien mit langfristigen Mietverträgen. 
                Von der Bewertung bis zum erfolgreichen Verkauf.
              </p>
            </div>
          </div>
        </section>

        {/* Definition */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="glass-card p-10">
              <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
                Was ist eine Lebensmittelmarkt-Immobilie?
              </h2>
              <p className="text-lg mb-6 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
                Lebensmittelmärkte (Supermarkt-Immobilien) sind Einzelhandelsimmobilien mit Fokus auf 
                Lebensmittel und Waren des täglichen Bedarfs. Sie zählen zu den <strong>stabilsten und krisensichersten</strong> Handelsimmobilien, 
                da sie Grundversorgung abdecken.
              </p>
              
              {/* Marktdaten */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 p-6 bg-blue-50 rounded-lg">
                <div className="text-center">
                  <p className="text-3xl font-bold mb-1" style={{ color: 'var(--gold-600)' }}>~36.000</p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Lebensmittelmärkte in Deutschland</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold mb-1" style={{ color: 'var(--gold-600)' }}>~1.400</p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>m² Ø Verkaufsfläche</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold mb-1" style={{ color: 'var(--gold-600)' }}>10-15</p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Jahre Ø Mietlaufzeit</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold mb-1" style={{ color: 'var(--gold-600)' }}>4-5%</p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Typische Rendite</p>
                </div>
              </div>
              
              <h3 className="text-xl font-bold mb-4 mt-8" style={{ color: 'var(--navy-700)' }}>Typische Objektmerkmale:</h3>
              <ul className="space-y-3">
                {objektmerkmale.map((merkmal, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span style={{ color: 'var(--gray-700)' }}>{merkmal}</span>
                  </li>
                ))}
              </ul>

              {/* Regionale Verteilung */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-700)' }}>Regionale Schwerpunkte:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <div>
                    <strong>Höchste Dichte:</strong> Nordrhein-Westfalen, Bayern, Baden-Württemberg (starke Kaufkraft, hohe Bevölkerungsdichte)
                  </div>
                  <div>
                    <strong>Investoren-Hotspots:</strong> Metropolregionen (München, Frankfurt, Hamburg, Rhein-Ruhr) und prosperierende Mittelstädte
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Mietvertragsstruktur */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Mietvertragsstruktur & Risiken
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--gray-600)' }}>
                Verstehen Sie die wichtigsten Vertragsbestandteile und worauf Käufer achten
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {mietvertragsstruktur.map((item, index) => (
                <Card key={index} className="glass-card p-6">
                  <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--navy-800)' }}>
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--gray-700)' }}>
                    {item.content}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Käufergruppen */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Käufergruppen & deren Erwartungen
              </h2>
            </div>

            <div className="space-y-6">
              {kaeufergruppen.map((kg, index) => (
                <Card key={index} className="glass-card p-8 border-l-4" style={{ borderLeftColor: 'var(--gold-600)' }}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--navy-800)' }}>
                        {kg.gruppe}
                      </h3>
                      <div className="inline-block px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                        Zielrendite: {kg.rendite}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <p style={{ color: 'var(--gray-700)' }}>{kg.erwartungen}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Bewertungslogik */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="glass-card p-10">
              <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
                Bewertungslogik auf hoher Ebene
              </h2>
              <p className="text-lg mb-6" style={{ color: 'var(--gray-700)' }}>
                Lebensmittelmärkte werden primär über das <strong>Ertragswertverfahren</strong> bewertet:
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg mb-6">
                <p className="text-center text-lg font-semibold mb-2" style={{ color: 'var(--navy-800)' }}>
                  Wert = Jahresmiete / Kapitalisierungszinssatz (Cap Rate)
                </p>
                <p className="text-center text-sm" style={{ color: 'var(--gray-600)' }}>
                  Beispiel: 100.000 € Jahresmiete bei 5% Cap Rate = 2.000.000 € Verkaufswert
                </p>
              </div>

              <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-700)' }}>Wertbeeinflussende Faktoren:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 mt-1" style={{ color: 'var(--gold-600)' }} />
                  <div>
                    <p className="font-semibold mb-1" style={{ color: 'var(--navy-700)' }}>Positiv:</p>
                    <p className="text-sm" style={{ color: 'var(--gray-700)' }}>Langer WAULT, Indexierung, bonitätsstarker Mieter, 1a-Lage, keine Konkurrenzschutzklauseln</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 mt-1" style={{ color: 'var(--error-600)' }} />
                  <div>
                    <p className="font-semibold mb-1" style={{ color: 'var(--navy-700)' }}>Negativ:</p>
                    <p className="text-sm" style={{ color: 'var(--gray-700)' }}>Kurzer WAULT, feste Miete ohne Anpassung, schwacher Standort, Sanierungsstau, auslaufende Verträge</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Unterlagen-Checkliste */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Unterlagen-Checkliste
              </h2>
              <p className="text-lg" style={{ color: 'var(--gray-600)' }}>Diese Dokumente benötigen Sie für einen erfolgreichen Verkauf</p>
            </div>

            <Card className="glass-card p-8">
              <ul className="space-y-3">
                {unterlagen.map((dok, index) => (
                  <li key={index} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <FileText className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--navy-600)' }} />
                    <span style={{ color: 'var(--gray-700)' }}>{dok}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </section>

        {/* Häufige Fehler */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Häufige Fehler vermeiden
              </h2>
            </div>

            <div className="space-y-6">
              {fehler.map((item, index) => (
                <Card key={index} className="glass-card p-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5" style={{ color: 'var(--error-600)' }} />
                        <h3 className="font-bold" style={{ color: 'var(--error-600)' }}>Fehler:</h3>
                      </div>
                      <p style={{ color: 'var(--gray-800)' }}>{item.fehler}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--navy-700)' }}>Folge:</h4>
                      <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.folge}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--navy-700)' }}>Vermeidung:</h4>
                      <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.vermeidung}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Häufig gestellte Fragen
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {faq.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left font-semibold text-lg" style={{ color: 'var(--navy-700)' }}>
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed" style={{ color: 'var(--gray-700)' }}>
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Tool Integration */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="glass-card p-10 text-center">
              <Calculator className="h-16 w-16 mx-auto mb-6" style={{ color: 'var(--gold-600)' }} />
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                WAULT kostenlos berechnen
              </h2>
              <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'var(--gray-700)' }}>
                Nutzen Sie unseren kostenlosen WAULT-Rechner und erhalten Sie eine erste Einschätzung 
                der Verkaufsfähigkeit Ihrer Lebensmittelmarkt-Immobilie.
              </p>
              <Link to="/tools/wault">
                <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-lg">
                  Jetzt WAULT berechnen
                  <ArrowRight className="ml-2 h-6 w-6" />
                </Button>
              </Link>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Bereit Ihren Lebensmittelmarkt zu verkaufen?
            </h2>
            <p className="text-lg mb-8 opacity-90">
              Vereinbaren Sie ein unverbindliches Erstgespräch und erfahren Sie, 
              welchen Wert Ihre Immobilie am Markt erzielen kann.
            </p>
            <Link to="/kontakt">
              <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-2xl">
                Jetzt Kontakt aufnehmen
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LebensmittelmaerkteSeite;